import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addSubmissionsResponseSchema,
  imageListSchema,
  type CompetitionStatus,
  type Submission,
  type UploadResult,
} from '@competa/shared';
import { apiRequest, apiUrl } from '../../api/client';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { ImageViewer } from '../../components/ImageViewer';
import viewerStyles from '../../components/ImageViewer.module.css';
import dialogStyles from '../../components/Dialog.module.css';
import { TextField } from '../../components/TextField';
import { errorMessage } from '../../i18n/errors';
import styles from './Competitions.module.css';
import { uploadInBatches, type StagedFile } from './upload';

/** Eigenschaften des Reiters „Bilder". */
interface ImagesTabProps {
  competitionId: number;
  status: CompetitionStatus;
  categories: string[];
  submissions: Submission[];
}

/**
 * Dialog zur Auswahl vorhandener Bilder aus der Bilddatenbank (bereits eingereichte sind ausgeblendet).
 * @param props Wettbewerb, Sichtbarkeit und Rückrufe.
 */
function LibraryDialog({
  competitionId,
  excluded,
  isOpen,
  onClose,
}: {
  competitionId: number;
  excluded: Set<number>;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const images = useQuery({
    queryKey: ['images', 'picker', q],
    queryFn: () =>
      apiRequest('GET', `/images?limit=200${q ? `&q=${encodeURIComponent(q)}` : ''}`, {
        schema: imageListSchema,
      }),
    enabled: isOpen,
  });
  const add = useMutation({
    mutationFn: () =>
      apiRequest('POST', `/competitions/${competitionId}/submissions`, {
        body: { imageIds: [...selected] },
        schema: addSubmissionsResponseSchema,
      }),
    onSuccess: async () => {
      setSelected(new Set());
      await queryClient.invalidateQueries({ queryKey: ['competition', competitionId] });
      onClose();
    },
  });
  const available = images.data?.items.filter((image) => !excluded.has(image.id)) ?? [];

  /** Schaltet die Auswahl eines Bildes um. */
  function toggle(id: number): void {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  return (
    <Dialog title={t('competitions.pickTitle')} isOpen={isOpen} onClose={onClose}>
      <TextField
        label={t('images.search')}
        type="search"
        value={q}
        onChange={(event) => setQ(event.target.value)}
      />
      {images.isPending && <p role="status">{t('common.loading')}</p>}
      {images.data && available.length === 0 && <p>{t('competitions.pickEmpty')}</p>}
      <ul className={styles.grid}>
        {available.map((image) => (
          <li key={image.id} className={styles.tile}>
            {image.hasThumb && (
              <img
                className={styles.tileImage}
                src={apiUrl(`/images/${image.id}/thumb`)}
                alt=""
                loading="lazy"
              />
            )}
            <label className={styles.tileCheck}>
              <input
                type="checkbox"
                checked={selected.has(image.id)}
                onChange={() => toggle(image.id)}
              />
              <span className={styles.tileTitle}>{image.title}</span>
            </label>
          </li>
        ))}
      </ul>
      {add.error && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, add.error)}
        </p>
      )}
      <div className={dialogStyles.actions}>
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button disabled={selected.size === 0 || add.isPending} onClick={() => add.mutate()}>
          {t('competitions.pickAdd', { count: selected.size })}
        </Button>
      </div>
    </Dialog>
  );
}

/**
 * Reiter „Bilder": Mehrfach-Upload mit Kategorie je Datei, Auswahl aus der Bilddatenbank und die
 * Liste der eingereichten Bilder (mit Entfernen, solange der Wettbewerb nicht abgeschlossen ist).
 * @param props Wettbewerb und Einreichungen.
 */
export function ImagesTab({ competitionId, status, categories, submissions }: ImagesTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [results, setResults] = useState<UploadResult[]>([]);
  const [dragging, setDragging] = useState(false);
  const [library, setLibrary] = useState(false);
  const [viewIndex, setViewIndex] = useState<number | null>(null);
  const closed = status === 'closed';

  const upload = useMutation({
    mutationFn: () =>
      uploadInBatches(`/competitions/${competitionId}/images`, staged, (partial) =>
        setResults(partial),
      ),
    onSuccess: async (all) => {
      setResults(all);
      setStaged(staged.filter((_, index) => all[index]?.status === 'error'));
      await queryClient.invalidateQueries({ queryKey: ['competition', competitionId] });
      await queryClient.invalidateQueries({ queryKey: ['images'] });
    },
  });
  const remove = useMutation({
    mutationFn: (submissionId: number) =>
      apiRequest('DELETE', `/competitions/${competitionId}/submissions/${submissionId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['competition', competitionId] }),
  });

  /** Merkt Dateien zum Hochladen vor (nur Bilddateien). */
  function stage(files: FileList | File[]): void {
    const images = [...files].filter((file) => /^image\/(jpeg|png|webp)$/.test(file.type));
    setResults([]);
    setStaged((current) => [...current, ...images.map((file) => ({ file, category: '' }))]);
  }

  /** Übernimmt die im Dateidialog gewählten Dateien. */
  function onPick(event: ChangeEvent<HTMLInputElement>): void {
    if (event.target.files) stage(event.target.files);
    event.target.value = '';
  }

  /** Übernimmt abgelegte Dateien. */
  function onDrop(event: DragEvent): void {
    event.preventDefault();
    setDragging(false);
    stage(event.dataTransfer.files);
  }

  /** Setzt die Kategorie einer vorgemerkten Datei. */
  function setCategory(index: number, category: string): void {
    setStaged(staged.map((item, i) => (i === index ? { ...item, category } : item)));
  }

  return (
    <div className={styles.panel}>
      {!closed && (
        <>
          <div
            className={`${styles.drop} ${dragging ? styles.dropActive : ''}`}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            <p>{t('competitions.dropHint')}</p>
            <input
              ref={input}
              className={styles.hidden}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              aria-label={t('competitions.chooseFiles')}
              onChange={onPick}
            />
            <div className={styles.actions}>
              <Button onClick={() => input.current?.click()}>
                {t('competitions.chooseFiles')}
              </Button>
              <Button variant="secondary" onClick={() => setLibrary(true)}>
                {t('competitions.fromLibrary')}
              </Button>
            </div>
          </div>

          {staged.length > 0 && (
            <div>
              <h2>{t('competitions.staged', { count: staged.length })}</h2>
              <datalist id="competition-categories">
                {categories.map((category) => (
                  <option key={category} value={category} />
                ))}
              </datalist>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('competitions.file')}</th>
                    <th>{t('competitions.category')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {staged.map((item, index) => (
                    <tr key={`${item.file.name}-${index}`}>
                      <td>{item.file.name}</td>
                      <td>
                        <input
                          className={styles.noteInput}
                          list="competition-categories"
                          aria-label={t('competitions.categoryOf', { name: item.file.name })}
                          value={item.category}
                          onChange={(event) => setCategory(index, event.target.value)}
                        />
                      </td>
                      <td>
                        <Button
                          variant="secondary"
                          onClick={() => setStaged(staged.filter((_, i) => i !== index))}
                        >
                          {t('common.delete')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className={styles.actions}>
                <Button disabled={upload.isPending} onClick={() => upload.mutate()}>
                  {upload.isPending ? t('competitions.uploading') : t('competitions.upload')}
                </Button>
                <Button variant="secondary" onClick={() => setStaged([])}>
                  {t('competitions.clearStaged')}
                </Button>
              </div>
            </div>
          )}

          {results.length > 0 && (
            <ul aria-label={t('competitions.uploadResults')}>
              {results.map((result, index) => (
                <li key={`${result.filename}-${index}`}>
                  {result.filename}:{' '}
                  {result.status === 'error' ? (
                    <span className={styles.error}>
                      {t(`errors.${result.code}`, { defaultValue: t('errors.unknown') })}
                    </span>
                  ) : (
                    <span className={styles.success}>
                      {t(`competitions.uploadStatus.${result.status}`)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {closed && <p>{t('competitions.closedHint')}</p>}

      <h2>{t('competitions.submitted', { count: submissions.length })}</h2>
      {submissions.length === 0 ? (
        <p>{t('competitions.noSubmissions')}</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('images.preview')}</th>
              <th>{t('images.titleLabel')}</th>
              <th>{t('competitions.category')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {submissions.map((submission) => (
              <tr key={submission.id}>
                <td>
                  {submission.hasThumb ? (
                    <button
                      type="button"
                      className={viewerStyles.thumbButton}
                      aria-label={t('viewer.open', { name: submission.imageTitle })}
                      onClick={() => setViewIndex(submissions.indexOf(submission))}
                    >
                      <img
                        className={styles.thumb}
                        src={apiUrl(`/images/${submission.imageId}/thumb`)}
                        alt=""
                        loading="lazy"
                      />
                    </button>
                  ) : (
                    <span className={`${styles.thumb} ${styles.thumbEmpty}`} />
                  )}
                </td>
                <td>{submission.imageTitle}</td>
                <td>{submission.category ?? ''}</td>
                <td>
                  {!closed && (
                    <Button
                      variant="secondary"
                      aria-label={t('competitions.removeSubmission', {
                        name: submission.imageTitle,
                      })}
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(submission.id)}
                    >
                      {t('competitions.remove')}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(remove.error || upload.error) && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, remove.error ?? upload.error)}
        </p>
      )}
      <ImageViewer
        items={submissions.map((s) => ({ id: s.imageId, title: s.imageTitle }))}
        index={viewIndex}
        onIndexChange={setViewIndex}
        onClose={() => setViewIndex(null)}
      />
      <LibraryDialog
        competitionId={competitionId}
        excluded={new Set(submissions.map((submission) => submission.imageId))}
        isOpen={library}
        onClose={() => setLibrary(false)}
      />
    </div>
  );
}
